import entries from "lodash/entries";
import values from "lodash/values";
import { proveNever } from "@glideapps/ts-necessities";

import { logError } from "../../debug-print";
import { AppDescription } from "../../components/SerializedApp";
import {
    ActionDescription,
    ArrayContentDescription,
    ArrayScreenDescription,
    getScreenComponents,
    InputOutputTables,
    isAddOrEditOrFormScreenDescription,
    isClassOrArrayScreenDescription,
    ScreenDescription,
    ScreenDescriptionKind,
} from "../description";
import { getEditOnSubmitActions } from "../form-on-submit";
import { handlerForComponentKind } from "../handlers";
import { ActionsRecord, SchemaInspector } from "../components";
import { ArrayScreenHandler, handlerForArrayScreenFormat } from "../array-screens";
import { handlerForActionKind } from ".";
import { makeInputOutputTablesForClassOrArrayScreen } from "../description-utils";

// If the schema inspector is not provided, the tables passed to the callback
// will be missing, but it will be called for all actions.
export function forEachActionInScreen(
    schema: SchemaInspector | undefined,
    screenName: string,
    screen: ScreenDescription,
    processAction: (action: ActionDescription, isFormAction: boolean, tables: InputOutputTables | undefined) => void
) {
    function processActions(
        actions: readonly ActionDescription[],
        isFormAction: boolean,
        tables: InputOutputTables | undefined
    ) {
        for (const action of actions) {
            processAction(action, isFormAction, tables);

            const handler = handlerForActionKind(action.kind);
            if (handler === undefined) {
                logError("No handler for action kind", action.kind);
                continue;
            }
            processActionsRecord(handler.getActions(action), tables);
        }
    }

    function processActionsRecord(record: ActionsRecord, tables: InputOutputTables | undefined) {
        for (const actions of values(record)) {
            processActions(actions, false, tables);
        }
    }

    if (!isClassOrArrayScreenDescription(screen)) return;

    const tables = makeInputOutputTablesForClassOrArrayScreen(screen, t => schema?.findTable(t));

    let arrayHandler: ArrayScreenHandler<ArrayContentDescription, ArrayScreenDescription> | undefined;
    if (screen.kind === ScreenDescriptionKind.Array) {
        arrayHandler = handlerForArrayScreenFormat(screen.format);
        if (arrayHandler !== undefined) {
            processActionsRecord(arrayHandler.getActions(screen, undefined), tables);
        }
    } else if (screen.kind === ScreenDescriptionKind.Class) {
        if (isAddOrEditOrFormScreenDescription(screenName, screen)) {
            processActions(getEditOnSubmitActions(screen), true, tables);
        }
    } else {
        // This can happen with primitive screens from legacy apps
        proveNever(screen, `Unknown screen kind ${(screen as any).kind}`, undefined);
    }

    for (const component of getScreenComponents(screen)) {
        const handler = handlerForComponentKind(component.kind);
        if (handler === undefined) {
            logError("No handler for component kind", component.kind);
            continue;
        }
        const actionsTables =
            tables !== undefined && schema !== undefined
                ? handler.getTablesForActions(tables, component, schema)?.[0]
                : tables;
        processActionsRecord(handler.getActions(component, tables?.input, schema, undefined), actionsTables);
    }
}

export function forEachActionInApp(
    app: AppDescription,
    processAction: (action: ActionDescription, screenName: string, isFormAction: boolean) => void
): void {
    for (const [screenName, screen] of entries(app.screenDescriptions)) {
        forEachActionInScreen(undefined, screenName, screen, (a, f) => processAction(a, screenName, f));
    }
}
